import React from 'react';
import ReactMarkdown from 'react-markdown';
import { HomeButton } from "../components/HomeButton"
import { getRequest } from '../api';
import { LoadingSpinner } from '../components/Loading';
import { sleep } from '../utils';

interface Hit_t {
  url: string;
  // the text on the page that matches the query
  snippet: string;
  // the ranges of the snippet that are relevant to the query, we bold this
  rangesToBold: Array<{ min: number, max: number }>;
  title: string;
  year?: number;
}

interface HitProps_t {
  hit: Hit_t;
}

interface ResultProps_t {
  hits: Hit_t[];
}

interface Region_t {
  bold: boolean;
  text: string;
}

const HitShower: React.FC<HitProps_t> = ({ hit }) => {
  return (
    <div className="hit-card">
      <div className="hit-header">
        <h4 className="hit-title">{hit.title}</h4>
        {/* Render the year if it exists */}
        {hit.year && <span className="hit-year">{hit.year}</span>}
      </div>
      
      {/* Target blank ensures links open in a new tab */}
      <a className="hit-url" href={hit.url} target="_blank" rel="noopener noreferrer">
        {hit.url}
      </a>

      <BoldPartOfString hit={hit} />
    </div>
  )
}

const ResultShower: React.FC<ResultProps_t> = (props: ResultProps_t) => {
  const items = props.hits.map((hit, i: number) => <HitShower hit={hit} />);

  return <div>{items}</div>;
}

export interface Filter_t {
  table: string,
  column: string,
  comparison: string,
  fuzzy: boolean,
}

type Category_t = "Music" | "Finance" | "Technical";

export interface Query_t {
  filters: Filter_t[],
  category: Category_t,
}

export interface SearchResponse_t {
  hits: Hit_t[],
  response: string,
}

const QueryComponent: React.FC<{ query: Query_t }> = ({ query }) => {
  if (!query) return null;

  return (
    <div className="query-info-container">
      {query.category && (
        <div className="query-category">
          Category: <strong>{query.category}</strong>
        </div>
      )}

      {query.filters && query.filters.length > 0 && (
        <div className="filters-container">
          {query.filters.map((filter, index) => (
            <div key={index} className="filter-badge">
              <span className="filter-table">{filter.table}</span>
              <span className="filter-divider">/</span>
              <span className="filter-column">{filter.column}</span>
              <span className="filter-comparison">{filter.comparison}</span>
              {filter.fuzzy && <span className="filter-fuzzy" title="Fuzzy Search">~</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Minor fix to BoldPartOfString to prevent <div>/<p> nesting issues
const BoldPartOfString: React.FC<HitProps_t> = (props: HitProps_t) => {
  const { hit } = props;
  let regions: Array<Region_t> = [];
  let start = 0;

  // Sort ranges to ensure they are processed in order
  const sortedRanges = [...hit.rangesToBold].sort((a, b) => a.min - b.min);

  for (const range of sortedRanges) {
    const { min, max } = range;
    if (min > start) {
      regions.push({ bold: false, text: hit.snippet.substring(start, min) });
    }
    regions.push({ bold: true, text: hit.snippet.substring(min, max) });
    start = max;
  }
  regions.push({ bold: false, text: hit.snippet.substring(start) });

  return (
    <div className="snippet-text">
      ...{regions.map((item, i) => (
        item.bold ? <strong key={i}>{item.text}</strong> : <span key={i}>{item.text}</span>
      ))}...
    </div>
  );
}

const SearchResponse: React.FC<{ response: SearchResponse_t }> = ({ response }) => {
  return (
    <div className="response-container">
      {/* The LLM Generative Answer */}
      <div className="llm-response-card">
        {/* TODO how to render as Markdown? */}
        <div className="llm-text">
          <ReactMarkdown>
            {response.response}
          </ReactMarkdown>
        </div>
      </div>
      {/* The Search Hits (Sources) */}
      <div className="sources-section">
        <h3>Sources</h3>
        <ResultShower hits={response.hits} />
      </div>
    </div>
  );
};

export interface SearchProps_t {
  input: string,
  query: Query_t | null,
  response: SearchResponse_t | null,
  loading: boolean,
}

export const SearchPage: React.FC<SearchProps_t> = (props) => {

  return (
    <div className='Search-wrapper'>
      <div className="search-header">
        <div className="search-container">
          <textarea
            /* TODO how to make the width take up the whole container but not hardcode this so it works on mobile? */
            className="modern-input modern-textarea"
            rows={5}
            value={props.input}
            readOnly
          />
      </div>

      {props.loading && <div style={{ padding: "8px"}}><LoadingSpinner /></div> }
      {props.query && <QueryComponent query={props.query} />}
      {props.response && <SearchResponse response={props.response} />}
      {props.response && 
      <div className="search-container">
          <textarea
            /* TODO how to make the width take up the whole container but not hardcode this so it works on mobile? */
            className="modern-input modern-textarea"
            rows={2}
            placeholder={'What else would you like to ask?'}
            value={""}
            readOnly
          />
      </div>}
      </div>
    </div>
  );
};